import { useState } from "react";
import type { ImpactEvent } from "../api";
import type { ImpactResult } from "../api";
import { Loading, Refused } from "../components/Shell";
import { useApi } from "../useApi";

interface Payload {
  events: ImpactEvent[];
  selected: string | null;
  claimed_days: number | null;
  result: ImpactResult | null;
  error: string | null;
  may_run: boolean;
}

/**
 * The narrative without its "Warnings:" block.
 *
 * The engine writes warnings into the narrative — right for the CLI and the
 * evidence export, which are read as one document — and the screen shows
 * them again in their own card. Printing the same paragraph twice on one
 * page teaches a reader to skip both.
 */
export function withoutWarnings(narrative: string): string {
  const lines = narrative.split("\n");
  const start = lines.findIndex((l) => l.trim() === "Warnings:");
  if (start < 0) return narrative;
  let end = start + 1;
  while (end < lines.length && lines[end]?.startsWith("    ")) end++;
  // The blank line that introduced the block goes with it.
  const from = start > 0 && !lines[start - 1]?.trim() ? start - 1 : start;
  return [...lines.slice(0, from), ...lines.slice(end)].join("\n");
}

export function Impact() {
  const [reference, setReference] = useState("");
  const { data, error, loading } = useApi<Payload>(
    `/impact${reference ? `?reference=${encodeURIComponent(reference)}` : ""}`);

  if (loading) return <Loading />;
  if (error) return <Refused status={error.status} detail={error.detail} />;
  if (!data) return null;
  const result = data.result;

  return (
    <>
      <h1>Schedule impact</h1>
      <p className="standfirst">
        Delay is the movement of planned Completion on the Accepted Programme
        current at the dividing date — measured by rescheduling, not asserted.
      </p>

      <nav className="chips">
        {data.events.map((e) => (
          <button key={e.reference} type="button"
            title={e.assessable === false
              ? `${e.title} — no fragnet supplied, so this cannot be assessed yet`
              : e.title}
            className={`chip${data.selected === e.reference ? " on" : ""}`
              + (e.assessable === false ? " noassess" : "")}
            onClick={() => setReference(e.reference)}>
            {e.reference}
          </button>
        ))}
      </nav>

      {data.error && (
        <div className="note amber"><span className="h">No assessment shown</span>{data.error}</div>
      )}

      {result && (
        <div className="split">
          <div>
            <section className="card">
              <h2 className="secttl">Did Completion move?</h2>
              {result.window && (
                <figure className="movement">
                  <figcaption className="k">
                    Planned Completion — window {result.window.label}
                  </figcaption>
                  <div className="axis">
                    <span className="gap" style={{
                      left: `${result.window.baseline_percent}%`,
                      width: `${(result.window.impacted_percent - result.window.baseline_percent).toFixed(2)}%`,
                    }} />
                    <span className="mark base" style={{ left: `${result.window.baseline_percent}%` }} />
                    <span className="mark moved" style={{ left: `${result.window.impacted_percent}%` }} />
                  </div>
                  <div className="axislabels">
                    <span>
                      <span className="k">
                        {result.is_accepted_programme
                          ? `Accepted Programme rev ${result.baseline_revision}`
                          : `Rev ${result.baseline_revision}, not accepted`}
                      </span>
                      <span className="mn">
                        {result.baseline_planned_completion}
                      </span>
                    </span>
                    <span className="now">
                      <span className="k">With the fragnet inserted</span>
                      {/* The moved date. Colour is earned here: this is the
                          deviation the whole assessment is about. */}
                      <span className="mn" style={{ color: "var(--soon)" }}>
                        {result.impacted_planned_completion}
                      </span>
                    </span>
                  </div>
                </figure>
              )}
              <div className="delayline">
                <span className="mn huge">{result.delay_days}</span>
                <div>
                  <strong>calendar days of delay to Completion</strong>
                  <div className="k">
                    {result.delay_working_days} working days · clause {result.clause}
                  </div>
                </div>
              </div>
            </section>

            <section className="card">
              <h2 className="secttl">Narrative</h2>
              <pre className="mono-doc">{withoutWarnings(result.narrative)}</pre>
              {result.warnings.length > 0 && (
                <div className="note amber">
                  <span className="h">Warnings from the analysis</span>
                  <ul>{result.warnings.map((w, i) => <li key={i}>{w}</li>)}</ul>
                </div>
              )}
            </section>
          </div>

          <aside>
            <section className="card">
              <h2 className="secttl">Against the quotation</h2>
              <div className="cmp">
                <span>Contractor's claim</span>
                <span className="mn">{data.claimed_days ?? "—"} days</span>
              </div>
              <div className="cmp">
                <span>Neev, clause {result.clause}</span>
                <span className="mn strong">{result.delay_days} days</span>
              </div>
              {/* The gap between what was claimed and what rescheduling
                  shows — the one number a reader is here to argue about. */}
              {data.claimed_days !== null && (
                <div className="cmp" style={{ color: "var(--soon)" }}>
                  <span>Difference</span>
                  <span className="mn strong">
                    {result.delay_days - data.claimed_days > 0 ? "+" : ""}
                    {result.delay_days - data.claimed_days} days
                  </span>
                </div>
              )}
            </section>

            <section className="card">
              <h2 className="secttl">Terminal float</h2>
              <div className="cmp">
                <span>Before</span><span className="mn">{result.terminal_float_before} days</span>
              </div>
              <div className="cmp">
                <span>After</span>
                {/* Negative terminal float: planned Completion is now past the
                    Completion Date. An abnormal condition, not a state. */}
                <span className="mn strong"
                  style={{ color: result.terminal_float_after < 0 ? "var(--now)" : undefined }}>
                  {result.terminal_float_after} days
                </span>
              </div>
              <p className="hint">
                That float is the Contractor's. It does not absorb this event:
                clause 63.5 assesses the movement of planned Completion whatever
                remains of it.
              </p>
            </section>

            {!result.is_accepted_programme && (
              <section className="card">
                <div className="note amber">
                  <span className="h">No Accepted Programme — clause {result.clause}</span>
                  {result.baseline_basis}
                </div>
              </section>
            )}

            <section className="card">
              <div className="note">
                <span className="h">Only a compensation event</span>
                An assessment runs against a notified compensation event and nothing
                else. An early warning has no dividing date; a programme submission
                is the baseline an assessment is measured <em>against</em>, never its
                trigger.
              </div>
            </section>
          </aside>
        </div>
      )}
    </>
  );
}
